import { useMemo } from "react";

import * as Sentry from "@sentry/react";
import { UseMutateFunction, useMutation } from "@tanstack/react-query";
import {
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query/src/types";
import { merge, uniqBy } from "lodash/fp";

import * as api from "@app/analytics/preferences/api";
import { UpdateRssLinkSettingsBody } from "@app/analytics/preferences/api";
import * as Events from "@app/analytics/tracking/events";
import { useAuth } from "@app/common/auth/useAuth";
import { DEFAULT_RSS_LINK_SETTINGS } from "@app/common/constants/rssLinks";
import { toast } from "@app/common/hooks/useToasts";
import type {
  User,
  UserSettings,
  UserMetadata,
  UserRssLinkSettings,
} from "@app/common/types";
import { combineUserMetadata } from "@app/common/utils";

const DEFAULT_USER_METADATA: UserMetadata = {};
const DEFAULT_USER_SETTINGS: UserSettings = {
  email: "",
  firstName: "",
  lastName: "",
  affectiveMetricsEnabled: false,
  afterCalendarEventRecapUpdateNotifyInvitees: false,
  afterMeetingUpdateCalendarEventWithRecap: false,
  analyticsOptIn: false,
  automaticRecordingEnabled: false,
  botConfig: {
    meetingScore: false,
    talkTime: false,
    timer: false,
    display: false,
  },
  botNameMode: "assistant",
  botNameModeCustomName: "",
  calendarOnlyJoinInternal: false,
  calendarPermissionLevel: "invite_only",
  externalParticipantAccessLevel: "viewer_full",
  internalParticipantAccessLevel: "editor",
  showMeetingEmailThumbnails: true,
  rssLinkSettings: DEFAULT_RSS_LINK_SETTINGS,
  shareLinkInBotMessage: false,
  storeRecordingOptIn: false,
  transcriptionOptIn: false,
  userDistributionSettings: [],
  userMetadata: DEFAULT_USER_METADATA,
};

const updateUserSettingsApi = (userSettings: api.UpdateUserSettingsBody) => {
  return api.updateUserSettings(userSettings, { withCreds: true });
};

const buildUpdateUserSettingsPayload = (
  user: User,
  userSettings: Partial<UserSettings>
): Partial<UserSettings> => {
  if (
    user.internalParticipantAccessLevel === "no_access" &&
    user.externalParticipantAccessLevel === "no_access"
  ) {
    return {
      ...userSettings,
      afterMeetingUpdateCalendarEventWithRecap: false,
    };
  }
  return userSettings;
};

export const defaultOnSuccess = () => {
  toast.success("Setting saved");
};

const mergeSettingsForOptimisticUpdate = (
  user: User,
  updatedSettings: Partial<UserSettings>
) => {
  if ("userDistributionSettings" in updatedSettings) {
    // user distribution setting updates only include the objects that are being updated,
    // so we need to merge and dedupe the individual settings
    const updatedUserDistributionSettings = uniqBy(
      (setting) => `${setting.method}-${setting.category}`,
      [
        ...(updatedSettings.userDistributionSettings ?? []),
        ...(user.userDistributionSettings ?? []),
      ]
    );
    return {
      ...user,
      ...updatedSettings,
      userDistributionSettings: updatedUserDistributionSettings,
    };
  }

  return { ...user, ...updatedSettings };
};

// This could be something we want in the common package
// but right now it's the only place we use it
const useMutationWithOverridableCallbacks = <
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
>({
  mutationFn,
  ...options
}: UseMutationOptions<TData, TError, TVariables, TContext>): UseMutationResult<
  TData,
  TError,
  TVariables,
  TContext
> => {
  const { onSuccess, onError, onSettled, ...otherOptions } = options ?? {};
  const { mutate, ...rest } = useMutation({
    mutationFn,
    onSettled,
    ...otherOptions,
  });

  const enhancedMutation: UseMutateFunction<
    TData,
    TError,
    TVariables,
    TContext
  > = (variables, mutOptions) => {
    return mutate(variables, {
      onSuccess: mutOptions?.onSuccess ?? onSuccess,
      onError: mutOptions?.onError ?? onError,
      ...otherOptions,
    });
  };

  return { mutate: enhancedMutation, ...rest };
};

export const useUserSettings = () => {
  const auth = useAuth();

  const updateUserSettings = useMutationWithOverridableCallbacks({
    mutationFn: (userSettings: Partial<UserSettings>) => {
      if (!auth.user) throw new Error("User must be set");
      const newSettings = buildUpdateUserSettingsPayload(
        auth.user,
        userSettings
      );
      Events.changeUserSettings(newSettings);
      return updateUserSettingsApi(newSettings);
    },
    onMutate: async (newUserSettings: Partial<UserSettings>) => {
      const previousUser = auth.user!;
      const newUser = mergeSettingsForOptimisticUpdate(
        previousUser,
        newUserSettings
      );
      auth.setUser(newUser);
      return { previousUser };
    },
    onSuccess: defaultOnSuccess,
    onError: (err, _newUserSettings, context) => {
      if (context) {
        auth.setUser(context.previousUser);
      }
      Sentry.captureException(err);
      toast.error(
        "Update failed, please try again or reach out to support@read.ai if the problem persists."
      );
    },
    onSettled: () => auth.fetchAndSetUser(),
  });

  /** Use in cases where we DO NOT want an optimistic update */
  const updateUserSettingsSync = useMutationWithOverridableCallbacks({
    mutationFn: (userSettings: Partial<UserSettings>) => {
      if (!auth.user) throw new Error("User must be set");
      const newSettings = buildUpdateUserSettingsPayload(
        auth.user,
        userSettings
      );
      Events.changeUserSettings(newSettings);
      return updateUserSettingsApi(newSettings);
    },
    onSuccess: defaultOnSuccess,
    onError: (err) => {
      Sentry.captureException(err);
      toast.error(
        "Update failed, please try again or reach out to support@read.ai if the problem persists."
      );
    },
    onSettled: () => {
      auth.fetchAndSetUser();
    },
  });

  const updateUserMetadata = useMutation({
    mutationFn: (userMetadata: Partial<UserMetadata>) =>
      updateUserSettingsApi(combineUserMetadata(auth.user!, userMetadata)),
    onMutate: (userMetadata: Partial<UserMetadata>) => {
      if (!auth.user) return;

      const previousUser = auth.user;
      const { userMetadata: combinedUserMetadata } = combineUserMetadata(
        auth.user,
        userMetadata
      );

      const user = {
        ...auth.user,
        userMetadata: combinedUserMetadata,
      };
      auth.setUser(user);
      return { previousUser };
    },
    onError: (err, userMetadata, context) => {
      Sentry.captureException(err);
      if (context?.previousUser) {
        auth.setUser(context.previousUser);
      }
    },
  });

  const updateRssLinkSettings = useMutation({
    mutationFn: (rssLinkSettings: Partial<UserRssLinkSettings>) =>
      updateUserSettingsApi({ rssLinkSettings }),
    onMutate: async (newRssLinkSettings: UpdateRssLinkSettingsBody) => {
      const previousSettings =
        auth.user!.rssLinkSettings ?? DEFAULT_RSS_LINK_SETTINGS;
      const newSettings = merge({ ...previousSettings }, newRssLinkSettings);

      const newUser = {
        ...auth.user!,
        rssLinkSettings: newSettings,
      };
      auth.setUser(newUser);
      return { previousSettings };
    },
    onError: (err) => Sentry.captureException(err),
    onSettled: auth.fetchAndSetUser,
  });

  /** Use in cases where we DO NOT want an optimistic update */
  const updateRssLinkSettingsSync = useMutation({
    mutationFn: (rssLinkSettings: Partial<UserRssLinkSettings>) =>
      updateUserSettingsApi({ rssLinkSettings }),
    onError: (err) => Sentry.captureException(err),
    onSettled: auth.fetchAndSetUser,
  });

  // TODO: remove memo and just use auth.user
  // once BE is updated to always return rssLinkSettings
  const userSettings: UserSettings = useMemo(
    () =>
      auth.user
        ? {
            rssLinkSettings: DEFAULT_USER_SETTINGS.rssLinkSettings,
            ...(auth.user as Omit<User, "rssLinkSettings">),
          }
        : DEFAULT_USER_SETTINGS,
    [auth.user]
  );

  return {
    updateUserSettings,
    updateUserSettingsSync,
    updateUserMetadata,
    updateRssLinkSettings,
    updateRssLinkSettingsSync,
    userSettings,
    userMetadata: userSettings.userMetadata ?? DEFAULT_USER_METADATA,
  };
};

export default useUserSettings;
